import classNames from 'classnames';
import { BatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { InfoTooltip } from 'components/shared/InfoTooltip';
import { CurrencyFlagIcon, WithCountryFlag } from 'components/shared/WithCountryFlag';
import type { ListFieldData, ListFieldMetadata } from 'models/forms/fields';
import { FC, useContext, useMemo } from 'react';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';
import { FieldDropDownList } from './ListField';

export const CurrencyListField: FC<ProductNodeBuilderProps> = ({
  node: { id },
  workspaceType,
  quoteWorkspaceVersion,
  mustForceAdvancedMode,
  tabIndex,
}) => {
  const fieldMetadata = useFieldMetadata<ListFieldMetadata>(id);
  const translationKey = useFieldTranslationKey(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const { isSelected } = useContext(BatchNavigationContext);

  const hasTooltip = useMemo(() => fieldMetadata?.hasTooltip ?? false, [fieldMetadata]);
  const isReadOnly = useMemo(() => fieldMetadata?.isReadOnly ?? false, [fieldMetadata]);

  const tooltipKey = hasTooltip ? `form.${translationKey}.tooltip` : undefined;
  const [value] = useData<ListFieldData>(id);

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as any}
      workspaceType={workspaceType}
      className={patchedFieldClassName}
    >
      {tooltipKey !== undefined && <InfoTooltip id={`tooltip-${id}`} tooltipKey={tooltipKey as any} />}
      <WithCountryFlag
        content={value ? <CurrencyFlagIcon currency={value} /> : undefined}
        className={classNames({ 'pe-none': isReadOnly })}
      >
        <FieldDropDownList
          id={id}
          workspaceType={workspaceType}
          quoteWorkspaceVersion={quoteWorkspaceVersion}
          mustForceAdvancedMode={mustForceAdvancedMode}
          tabIndex={tabIndex}
          isSelected={isSelected}
        />
      </WithCountryFlag>
    </BlockOrientationFormGroup>
  );
};
CurrencyListField.displayName = 'CurrencyListField';
